var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle, } from "react";
import { createUseStyles } from "react-jss";
import { SearchSize } from ".";
import { useTranslation } from "./hooks/useTranslation";
import { THEME } from "./theming/theming";
import { classNames } from "./utils/classNames";
import { keyPress } from "./utils/keyPress";
import { useDebounce } from "./utils/useDebounce";
export var Search = forwardRef(function (_a, ref) {
    var _b = _a.initialValue, initialValue = _b === void 0 ? "" : _b, onChange = _a.onChange, label = _a.label, _c = _a.debounceTime, debounceTime = _c === void 0 ? 500 : _c, _d = _a.uniqueId, uniqueId = _d === void 0 ? 0 : _d, componentSize = _a.componentSize, ariaDescription = _a.ariaDescription, _e = _a.iconPosition, iconPosition = _e === void 0 ? "right" : _e;
    var classes = useSearchStyles(componentSize, iconPosition);
    var t = useTranslation();
    var _f = useState(initialValue), value = _f[0], setValue = _f[1];
    var debouncedValue = useDebounce(value, debounceTime);
    var useFocus = function () {
        var htmlElRef = useRef(null);
        var setFocus = function () {
            htmlElRef.current && htmlElRef.current.focus();
        };
        return [htmlElRef, setFocus];
    };
    var _g = useFocus(), inputRef = _g[0], setInputFocus = _g[1];
    useEffect(function () {
        onChange(debouncedValue);
    }, [onChange, debouncedValue]);
    var keysPressed = {};
    var keyFunctions = useCallback(function (event) {
        keysPressed[event.keyCode] = true;
        if (keysPressed[keyPress.CONTROL] &&
            event.keyCode === keyPress.BACKSLASH) {
            setTimeout(function () { return setInputFocus(); }, 1);
        }
        if (value && event.keyCode === keyPress.ESCAPE) {
            onChange("");
            onClear();
        }
    }, [keysPressed, value, setInputFocus, onChange]);
    useEffect(function () {
        document.addEventListener("keydown", keyFunctions, false);
        document.addEventListener("keyup", function (event) {
            delete keysPressed[event.keyCode];
        });
        return function () {
            document.removeEventListener("keydown", keyFunctions, false);
        };
    }, [keyFunctions, keysPressed]);
    var onClear = function () { return setValue(""); };
    useImperativeHandle(ref, function () { return ({
        resetSearch: function () {
            setValue("");
        },
    }); });
    return (React.createElement("div", { className: "".concat(classes.root, " search"), role: "search" },
        React.createElement("label", { className: classes.label, htmlFor: "searchBar".concat(uniqueId) },
            "Search ",
            label,
            ariaDescription && ", ".concat(ariaDescription)),
        React.createElement("input", { type: "search", placeholder: "".concat(t("Search"), " ").concat(label, "..."), onChange: function (ev) {
                setValue(ev.target.value);
            }, value: value, ref: inputRef, id: "searchBar".concat(uniqueId), className: classes.input, role: "searchbox" }),
        value && (React.createElement("span", { role: "button", className: classNames("icon-remove", classes.remove, "glyphicon-pro", "glyphicon-pro-times"), "aria-label": t("Clear Search"), tabIndex: 0, onClick: onClear, onKeyPress: onClear })),
        React.createElement("span", { className: classNames(classes.icon, "glyphicon-search", "icon-search"), "aria-hidden": "true" })));
});
var useSearchStyles = function (componentSize, iconPosition) {
    var _a = THEME.palette, gray = _a.gray, primary = _a.primary;
    var icon = {
        top: 7,
        right: 9,
        position: "absolute",
        color: primary.base.color,
        fontFamily: "Glyphicons Halflings",
        fontStyle: "normal",
        fontWeight: 400,
    };
    var remove = { top: 6.5, right: 32, position: "absolute", fontSize: 18 };
    var input = {
        fontSize: 14,
        padding: {
            top: 6,
            right: 10,
            bottom: 7,
            left: 16,
        },
        width: "100%",
        border: 0,
        background: "transparent",
        color: gray.charcoal.color,
        "&:placeholder": {
            color: gray.silver.color,
        },
        "&:-ms-input-placeholder": {
            color: gray.silver.color,
        },
        height: 35,
    };
    if (componentSize === SearchSize.LG) {
        icon = __assign(__assign({}, icon), { top: 10 });
        remove = __assign(__assign({}, remove), { top: 10 });
        input = __assign(__assign({}, input), { height: 40, padding: __assign(__assign({}, input.padding), { top: 5 }) });
    }
    if (componentSize === SearchSize.LG && iconPosition === "left") {
        icon = __assign(__assign({}, icon), { left: 12 });
        remove = __assign(__assign({}, remove), { right: 10 });
    }
    if (componentSize === SearchSize.SM) {
        icon = __assign(__assign({}, icon), { top: 5 });
        remove = __assign(__assign({}, remove), { right: 28, top: 5 });
        input = __assign(__assign({}, input), { height: 30, padding: {
                top: 6,
                left: 12,
                right: 46,
                bottom: 7,
            } });
    }
    if (iconPosition === "left") {
        icon = __assign(__assign({}, icon), { right: undefined, left: 9 });
        input = __assign(__assign({}, input), { padding: __assign(__assign({}, input.padding), { left: 36, right: 14 }) });
        remove = __assign(__assign({}, remove), { right: 9 });
    }
    var useStyles = createUseStyles({
        root: {
            position: "relative",
            backgroundColor: gray.white.color,
            border: {
                width: 1,
                color: gray.smoke.color,
                style: "solid",
            },
        },
        label: { position: "absolute", left: -99999 },
        input: input,
        icon: icon,
        remove: remove,
    });
    var classes = useStyles();
    return classes;
};
